import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Avatar,
  ListItemButton,
  useMediaQuery,
  CssBaseline,
  ThemeProvider,
  createTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  ShoppingCart as OrdersIcon,
  Assessment as AnalyticsIcon,
  Store as StoreIcon,
  Logout as LogoutIcon,
  Inventory as ProductIcon,
  People as CustomerIcon,
  Campaign as MarketingIcon,
  Home as HomeIcon,
  Notifications as NotificationsIcon,
  Category as CategoryIcon,
  FileUpload as ImportIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  CreditCard as BillingIcon,
  MenuBook as MenuBookIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

const drawerWidth = 272;

const getMenuItems = (t) => [
  { title: t('nav.dashboard'), icon: <DashboardIcon />, path: '/admin/dashboard' },
  { divider: true, label: t('nav.products_section') },
  { title: t('nav.products'), icon: <ProductIcon />, path: '/admin/products' },
  { title: t('nav.categories'), icon: <CategoryIcon />, path: '/admin/categories' },
  { title: t('nav.import'), icon: <ImportIcon />, path: '/admin/import' },
  { title: t('nav.orders'), icon: <OrdersIcon />, path: '/admin/orders' },
  { title: t('nav.members'), icon: <CustomerIcon />, path: '/admin/members' },
  { divider: true, label: t('nav.reports_section') },
  { title: t('nav.analytics'), icon: <AnalyticsIcon />, path: '/admin/analytics' },
  { title: t('nav.remarketing'), icon: <MarketingIcon />, path: '/admin/remarketing' },
  { divider: true, label: t('nav.settings_section') },
  { title: t('nav.store_settings'), icon: <StoreIcon />, path: '/admin/tenant-settings' },
  { title: t('nav.notifications'), icon: <NotificationsIcon />, path: '/admin/notifications' },
  { title: t('nav.billing'), icon: <BillingIcon />, path: '/admin/billing' },
  { title: t('nav.manual', { defaultValue: '操作手冊' }), icon: <MenuBookIcon />, path: '/admin/manual' },
];

const sharedTypography = {
  fontFamily: '"Plus Jakarta Sans", "Noto Sans TC", "Noto Sans JP", "Noto Sans KR", sans-serif',
};

const createAdminTheme = (darkMode) => createTheme({
  palette: darkMode
    ? {
      mode: 'dark',
      primary: { main: '#14B8A6' },
      secondary: { main: '#F4A261' },
      background: { default: '#0B1220', paper: '#111A2C' },
    }
    : {
      mode: 'light',
      primary: { main: '#0F766E' },
      secondary: { main: '#E76F51' },
      background: { default: '#F3F6FA', paper: '#FFFFFF' },
      text: { primary: '#0F172A', secondary: '#475569' },
    },
  shape: { borderRadius: 14 },
  typography: sharedTypography,
});

const AdminLayout = ({ children }) => {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('admin_dark_mode') === '1');

  const adminTheme = useMemo(() => createAdminTheme(darkMode), [darkMode]);
  const isMobile = useMediaQuery(adminTheme.breakpoints.down('md'));
  const menuItems = useMemo(() => getMenuItems(t), [t]);

  const toggleDarkMode = () => {
    setDarkMode((prev) => {
      const next = !prev;
      localStorage.setItem('admin_dark_mode', next ? '1' : '0');
      return next;
    });
  };

  const handleMenuClick = (item) => {
    navigate(item.path);
    if (isMobile) setMobileOpen(false);
  };

  const isActive = (path) => location.pathname === path;

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 45, height: 45, boxShadow: '0 8px 18px rgba(15,118,110,0.35)' }}>
          <StoreIcon />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={800} sx={{ fontSize: '0.95rem' }}>
            {t('nav.system_title')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('nav.admin_panel')}
          </Typography>
        </Box>
        <LanguageSwitcher sx={{ mr: 0.5 }} />
        <IconButton onClick={toggleDarkMode} size="small" title={darkMode ? t('nav.light_mode') : t('nav.dark_mode')}>
          {darkMode ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
        </IconButton>
      </Box>

      <Divider />

      <List sx={{ flex: 1, px: 1, py: 1 }}>
        {menuItems.map((item, index) => {
          if (item.divider) {
            return (
              <Box key={index}>
                <Divider sx={{ my: 1 }} />
                {item.label && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ px: 2, py: 0.5, display: 'block', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem' }}
                  >
                    {item.label}
                  </Typography>
                )}
              </Box>
            );
          }

          return (
            <ListItemButton
              key={item.title}
              onClick={() => handleMenuClick(item)}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                bgcolor: isActive(item.path) ? 'primary.main' : 'transparent',
                color: isActive(item.path) ? 'white' : 'inherit',
                border: isActive(item.path) ? '1px solid rgba(255,255,255,0.18)' : '1px solid transparent',
                '&:hover': {
                  bgcolor: isActive(item.path) ? 'primary.dark' : 'action.hover',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: isActive(item.path) ? 'white' : 'inherit' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.title} />
            </ListItemButton>
          );
        })}
      </List>

      <Divider />

      <List sx={{ px: 1, py: 1 }}>
        <ListItemButton onClick={() => window.open('/', '_blank')} sx={{ borderRadius: 2, mb: 0.5 }}>
          <ListItemIcon sx={{ minWidth: 40 }}><HomeIcon /></ListItemIcon>
          <ListItemText primary={t('nav.go_to_store')} />
        </ListItemButton>
        <ListItemButton
          onClick={() => {
            localStorage.removeItem('admin_token');
            navigate('/admin');
          }}
          sx={{ borderRadius: 2, color: 'error.main' }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: 'error.main' }}><LogoutIcon /></ListItemIcon>
          <ListItemText primary={t('nav.logout')} />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <ThemeProvider theme={adminTheme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
        <AppBar
          position="fixed"
          sx={{ display: { md: 'none' }, bgcolor: 'background.paper', color: 'text.primary', boxShadow: '0 8px 28px rgba(15, 23, 42, 0.08)' }}
        >
          <Toolbar>
            <IconButton color="inherit" edge="start" onClick={() => setMobileOpen((v) => !v)} sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap sx={{ flex: 1 }}>{t('nav.system_title')}</Typography>
            <LanguageSwitcher />
            <IconButton onClick={toggleDarkMode} size="small" title={darkMode ? t('nav.light_mode') : t('nav.dark_mode')}>
              {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Toolbar>
        </AppBar>

        <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                bgcolor: 'background.paper',
                backgroundImage: darkMode
                  ? 'linear-gradient(180deg, rgba(20,184,166,0.06), rgba(17,26,44,0.96) 35%)'
                  : 'linear-gradient(180deg, rgba(15,118,110,0.06), rgba(255,255,255,0.98) 35%)',
              },
            }}
          >
            {drawer}
          </Drawer>

          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                borderRight: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                backgroundImage: darkMode
                  ? 'linear-gradient(180deg, rgba(20,184,166,0.06), rgba(17,26,44,0.96) 35%)'
                  : 'linear-gradient(180deg, rgba(15,118,110,0.06), rgba(255,255,255,0.98) 35%)',
              },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: { md: `calc(100% - ${drawerWidth}px)` },
            mt: { xs: 8, md: 0 },
            minHeight: '100vh',
          }}
        >
          {children}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default AdminLayout;

